import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { Activity, History, Settings, LogOut, User, Menu, Info, MessageSquare, Award, BarChart3, Moon, Sun, Monitor } from 'lucide-react';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { isDark, themeMode, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  // Get theme icon based on mode
  const getThemeIcon = () => {
    if (themeMode === 'system') {
      return <Monitor className="h-4 w-4 text-sky-500" />;
    }
    if (themeMode === 'dark' || (themeMode === 'system' && isDark)) {
      return <Sun className="h-4 w-4 text-amber-400" />;
    }
    return <Moon className="h-4 w-4 text-slate-600" />;
  };

  const getThemeTitle = () => {
    if (themeMode === 'light') return 'Light mode (click for dark)';
    if (themeMode === 'dark') return 'Dark mode (click for system)';
    return 'System mode (click for light)';
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-40 backdrop-blur-xl bg-white/80 dark:bg-slate-900/95 border-b border-slate-200/50 dark:border-slate-700/50">
      <nav className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <img 
            src="/images/logo.png"
            alt="NoSpin-EBM" 
            className="w-8 h-8 object-contain"
          />
          <span className="font-semibold text-xl text-slate-900 dark:text-slate-100">NoSpin-EBM</span>
        </Link>

        {user ? (
          <div className="flex items-center gap-2 md:gap-4">
            <div className="hidden md:flex items-center gap-1">
              <Link to="/">
                <Button 
                  variant={isActive('/') ? 'secondary' : 'ghost'} 
                  size="sm"
                  className="rounded-full"
                  data-testid="nav-search"
                >
                  Search
                </Button>
              </Link>
              <Link to="/history">
                <Button 
                  variant={isActive('/history') ? 'secondary' : 'ghost'} 
                  size="sm"
                  className="rounded-full"
                  data-testid="nav-history"
                >
                  <History className="h-4 w-4 mr-1" />
                  History
                </Button>
              </Link>
            </div>

            {user.is_ambassador && (
              <span className="px-3 py-1 bg-gradient-to-r from-emerald-100 to-sky-100 dark:from-emerald-900/50 dark:to-sky-900/50 text-emerald-700 dark:text-emerald-300 text-xs font-medium rounded-full">
                Ambassador
              </span>
            )}

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="rounded-full w-9 h-9 p-0"
              data-testid="theme-toggle"
              title={getThemeTitle()}
            >
              {getThemeIcon()}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="rounded-full" data-testid="nav-menu">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                    <User className="h-4 w-4 text-slate-600" />
                  </div>
                  <Menu className="h-4 w-4 ml-1 md:hidden" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="font-medium text-sm">{user.name}</p>
                  <p className="text-xs text-slate-500">{user.email}</p>
                  {user.subscription_status === 'pro' && (
                    <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                      Pro
                    </span>
                  )}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/')} className="md:hidden">
                  <Activity className="h-4 w-4 mr-2" />
                  Search
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/history')} className="md:hidden">
                  <History className="h-4 w-4 mr-2" />
                  History
                </DropdownMenuItem>
                <DropdownMenuSeparator className="md:hidden" />
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/cme')}>
                  <Award className="h-4 w-4 mr-2" />
                  CME Credits
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/feedback')}>
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Feedback
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/about')}>
                  <Info className="h-4 w-4 mr-2" />
                  About
                </DropdownMenuItem>
                {user.is_admin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="nav-admin">
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Admin Dashboard
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {/* Theme Toggle for non-authenticated users */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="rounded-full w-9 h-9 p-0"
              data-testid="theme-toggle"
              title={getThemeTitle()}
            >
              {getThemeIcon()}
            </Button>
            <Link to="/login">
              <Button variant="ghost" size="sm" className="rounded-full" data-testid="nav-login">
                Sign In
              </Button>
            </Link>
            <Link to="/register">
              <Button size="sm" className="rounded-full bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-slate-200 text-white dark:text-slate-900" data-testid="nav-register">
                Get Started
              </Button>
            </Link>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
